import { type PlanId, planLabels } from '@/lib/branchTypes'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'

// Source-of-truth content for the end-of-Plan-A timeline diagram.

// The plan forming the spine — the line itself, not a fork branch.
export const TIMELINE_SPINE_PLAN: PlanId = 'A'

export type TimelinePlanEntry = {
  planId: PlanId
  label: string
  side: 'above' | 'below'
}

export const TIMELINE_FORK_PLANS: TimelinePlanEntry[] = [
  { planId: 'S', label: 'Plan S', side: 'above' },
  { planId: 'B', label: 'Plan B', side: 'below' },
  { planId: 'C', label: 'Plan C', side: 'below' },
  { planId: 'D', label: 'Plan D', side: 'below' },
]

export const TIMELINE_SPINE_LABEL = 'Plan A'

// Hover-tooltip gist for each plan button, keyed by plan.
export const PLAN_TOOLTIP: Record<PlanId, string> = {
  A: 'The Deal — the US offers China and other nations a deal to mutually verify and control AI development.',
  S: 'Stop — the US seeks a global moratorium that halts frontier AI development worldwide.',
  B: 'Sabotage — take aggressive action to slow China down, buying years rather than months.',
  C: 'The Slowdown — pause for a few months during the intelligence explosion to make progress on alignment.',
  D: 'The Race — race about as hard as possible to beat China and rival companies to superintelligence.',
}

// Keyed off shared planLabels so it can't drift.
export const PLAN_SUBTITLE: Record<PlanId, string> = Object.fromEntries(
  planLabels.map(p => [p.id, p.subtitle]),
) as Record<PlanId, string>

export type TimelineBranchpointEntry = {
  label: string
  // 'details' id = slugify(summary); 'branchpoint' id = `branchpoint-${slugify(title)}` (see BranchpointBox).
  detailsId: string
  kind: 'details' | 'branchpoint'
}

// detailsId must equal a live slug on the Plan A path or the button silently no-ops.
// Slug = slugify(summary, {lower:true, strict:true}) (see app/common/Markdown.tsx).
export const TIMELINE_BRANCHPOINTS: TimelineBranchpointEntry[] = [
  { label: 'Covert Project', kind: 'branchpoint', detailsId: 'branchpoint-china-attempts-a-covert-project' },
  { label: 'Deal Breakdown', kind: 'branchpoint', detailsId: 'branchpoint-deal-breakdown' },
  { label: 'Incompetence', kind: 'branchpoint', detailsId: 'branchpoint-flawed-safety-case-is-approved' },
]

// Hover-tooltip gist for each branchpoint button, keyed by detailsId.
export const BRANCHPOINT_TOOLTIP: Record<string, string> = {
  'branchpoint-china-attempts-a-covert-project': 'China defects from the deal and attempts a secret intelligence explosion.',
  'branchpoint-deal-breakdown': 'The deal breaks down as political circumstances shift in the US or China.',
  'branchpoint-flawed-safety-case-is-approved': 'An unsafe AI design is approved on the strength of a flawed safety case.',
}

export type TimelineEndpointEntry =
  | { kind: 'epilogue'; label: string }
  | { kind: 'perspective'; label: string; perspective: PerspectiveCategory }

// "main scenario" perspective is the spine, so it's not listed here.
export const TIMELINE_ENDPOINTS: TimelineEndpointEntry[] = [
  { kind: 'epilogue', label: 'Epilogue' },
  { kind: 'perspective', label: 'Public POV', perspective: 'public' },
  { kind: 'perspective', label: 'Insider POV', perspective: 'insider' },
]

// POV buttons reuse the shared PERSPECTIVE_TAB_TOOLTIPS at their render site.
export const EPILOGUE_TOOLTIP = 'Life after superintelligence — how the world looks once Plan A has succeeded.'

// Epilogue rides the spine itself, so it's pulled out for the spine-end label.
export const EPILOGUE_ENTRY = TIMELINE_ENDPOINTS.find(e => e.kind === 'epilogue')

// DOM id of the Plan A epilogue heading (see PlaybookEpilogue.tsx).
export const EPILOGUE_HEADING_ID = 'playbook-epilogue'

// DOM id of the hub end cap's "You've reached the end of Plan A" heading
// (the ScenarioEndFooter the ending bodies expand under). A runtime
// ending-open smooth-scrolls to frame this heading at the top of the
// viewport so the diagram + nav buttons stay in view while the body
// animates open below them (see useAnchorDeepLink).
export const SCENARIO_END_HUB_HEADING_ID = 'scenario-end-hub'

// DOM id of each inline-perspective body, keyed by perspective.
export const POV_HEADING_IDS: Record<Exclude<PerspectiveCategory, 'mainScenario'>, string> = {
  public: 'playbook-public-pov',
  insider: 'playbook-insider-pov',
}

// Shared props for both the desktop and mobile diagram.
export type PlanATimelineDiagramProps = {
  currentPlan: PlanId | null
  selectPlan: (planId: PlanId) => void
  setEpilogueExpanded: (expanded: boolean) => void
}
